import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SquareCatalogProduct } from "../types/SquareCatalog";
export type CartItem = {
  /** Square variationId — preserved for checkout. */
  variationId: string;
  itemId: string;
  name: string;
  variationName: string;
  imageUrl: string | null;
  priceCents: number | null;
  currency: string;
  /** Inventory at the time the item was last added; caps the quantity. */
  inventory: number;
  quantity: number;
};
type CartContextValue = {
  items: CartItem[];
  itemCount: number;
  subtotalCents: number;
  currency: string;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  getQuantity: (variationId: string) => number;
  /** Adds (positive) or removes (negative) units of a product, capped by inventory. */
  changeQuantity: (product: SquareCatalogProduct, delta: number) => void;
  setItemQuantity: (variationId: string, quantity: number) => void;
  removeItem: (variationId: string) => void;
  clearCart: () => void;
};
const storageKey = "pickens-passion-cart";
const CartContext = createContext<CartContextValue | null>(null);
function loadCart(): CartItem[] {
  try {
    const stored = window.localStorage.getItem(storageKey);
    const parsed: unknown = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? (parsed as CartItem[]).filter(item => item && typeof item.variationId === "string" && item.quantity > 0) : [];
  } catch {
    return [];
  }
}
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>(loadCart);
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(items));
    } catch {
      // Storage unavailable (private mode) — cart still works for this visit.
    }
  }, [items]);
  const changeQuantity = useCallback((product: SquareCatalogProduct, delta: number) => {
    setItems(current => {
      const existing = current.find(item => item.variationId === product.variationId);
      const nextQuantity = Math.min(product.inventory, Math.max(0, (existing?.quantity ?? 0) + delta));
      if (nextQuantity === 0) {
        return current.filter(item => item.variationId !== product.variationId);
      }
      const nextItem: CartItem = {
        variationId: product.variationId,
        itemId: product.itemId,
        name: product.name,
        variationName: product.variationName,
        imageUrl: product.imageUrl,
        priceCents: product.priceCents,
        currency: product.currency,
        inventory: product.inventory,
        quantity: nextQuantity
      };
      return existing ? current.map(item => item.variationId === product.variationId ? nextItem : item) : [...current, nextItem];
    });
  }, []);
  const setItemQuantity = useCallback((variationId: string, quantity: number) => {
    setItems(current => current.flatMap(item => {
      if (item.variationId !== variationId) {
        return [item];
      }
      const nextQuantity = Math.min(item.inventory, Math.max(0, quantity));
      return nextQuantity === 0 ? [] : [{
        ...item,
        quantity: nextQuantity
      }];
    }));
  }, []);
  const removeItem = useCallback((variationId: string) => {
    setItems(current => current.filter(item => item.variationId !== variationId));
  }, []);
  const value = useMemo<CartContextValue>(() => {
    const itemCount = items.reduce((total, item) => total + item.quantity, 0);
    const subtotalCents = items.reduce((total, item) => total + (item.priceCents ?? 0) * item.quantity, 0);
    return {
      items,
      itemCount,
      subtotalCents,
      currency: items[0]?.currency ?? "USD",
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      getQuantity: variationId => items.find(item => item.variationId === variationId)?.quantity ?? 0,
      changeQuantity,
      setItemQuantity,
      removeItem,
      clearCart: () => setItems([])
    };
  }, [items, isOpen, changeQuantity, setItemQuantity, removeItem]);
  return <CartContext.Provider value={value} data-id="element-405" data-mp="oybpfep54aze">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used inside CartProvider");
  }
  return context;
}